import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from 'lucide-react';
import type * as React from 'react';
import { Toaster as Sonner, type ToasterProps, toast } from 'sonner';

import { cn } from '#lib/cn.ts';

/**
 * Three deliberate departures from the registry version:
 *
 * 1. No `next-themes`. The CLI wires `useTheme()` into the `theme` prop; there is no Next.js here
 *    and the colour scheme is the OS preference read in CSS (see the `dark` custom variant in
 *    `styles/tailwind.css`). The `--normal-*` variables below point at tokens that already flip, but
 *    Sonner's `theme` is not only its stock palette: `[data-sonner-theme='light']` hard-codes the
 *    description colour (`#3f3f3f`), unreadable on a dark `--popover`. An app that renders dark
 *    passes `theme='system'` (`apps/web`), which tracks the same media query the CSS does; a
 *    light-only app leaves Sonner's `light` default.
 *
 * 2. Success, error and warning carry a level accent. The registry version styles every level the
 *    same, which is fine next to a `@mantine/notifications` that did not — but it was Mantine's
 *    `color` prop drawing the stripe, so replacing it with the stock Toaster left every outcome
 *    looking identical. `--success` and `--warning` were added to the theme for this; both are
 *    darkened from their brand/Mantine bases in the light scheme to clear 3:1 on `--popover`.
 */
function Toaster({ toastOptions, ...props }: ToasterProps) {
  return (
    <Sonner
      className='toaster group'
      icons={{
        success: <CircleCheckIcon className='size-4' />,
        info: <InfoIcon className='size-4' />,
        warning: <TriangleAlertIcon className='size-4' />,
        error: <OctagonXIcon className='size-4' />,
        loading: <Loader2Icon className='size-4 animate-spin' />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      {...props}
      toastOptions={{
        ...toastOptions,
        classNames: {
          ...toastOptions?.classNames,
          toast: cn('cn-toast', toastOptions?.classNames?.toast),
          /* Sonner's own description rule sets no `white-space`, so a description built by joining
             several messages with `\n` would collapse into one run-on line. */
          description: cn('whitespace-pre-line', toastOptions?.classNames?.description),
          /* Level accents. The rules live in `styles/tailwind.css` — see the comment there for why
             they cannot be utilities. `info` and `loading` stay neutral: neither is an outcome. */
          success: cn('cn-toast-success', toastOptions?.classNames?.success),
          error: cn('cn-toast-error', toastOptions?.classNames?.error),
          warning: cn('cn-toast-warning', toastOptions?.classNames?.warning),
        },
      }}
    />
  );
}

/*
 * Re-exported so call sites reach the imperative API through the design system rather than adding
 * `sonner` to their own dependencies.
 */
export { Toaster, toast };
